/**
 * CodeMirror Editor Component
 *
 * A wrapper around CodeMirror 6 for editing Python code in the MCP Admin panel.
 * Features:
 * - Python syntax highlighting
 * - Find/Replace with highlighting (Ctrl/Cmd+F)
 * - Line numbers
 * - Dark theme matching the app
 * - Auto-indent and bracket matching
 *
 * Usage:
 *   import CodeMirrorEditor from './CodeMirrorEditor';
 *   <CodeMirrorEditor
 *     value={code}
 *     onChange={setCode}
 *     height="400px"
 *     readOnly={false}
 *   />
 */

import React, { useCallback } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { search, searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { keymap } from "@codemirror/view";

/**
 * CodeMirror Editor for Python code
 *
 * @param {Object} props
 * @param {string} props.value - The code content
 * @param {function} props.onChange - Callback when code changes
 * @param {string} [props.height="400px"] - Editor height
 * @param {boolean} [props.readOnly=false] - Whether the editor is read-only
 * @param {string} [props.placeholder] - Placeholder text when empty
 */
const CodeMirrorEditor = ({
  value,
  onChange,
  height = "400px",
  readOnly = false,
  placeholder = "",
}) => {
  const handleChange = useCallback(
    (val) => {
      if (onChange) {
        onChange(val);
      }
    },
    [onChange]
  );

  // Extensions for the editor
  const extensions = [
    python(),
    search(),
    highlightSelectionMatches(),
    keymap.of(searchKeymap),
  ];

  return (
    <div
      style={{
        borderRadius: "4px",
        overflow: "hidden",
        border: "1px solid var(--border-primary)",
      }}
    >
      <CodeMirror
        value={value}
        height={height}
        theme={oneDark}
        extensions={extensions}
        onChange={handleChange}
        readOnly={readOnly}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightSpecialChars: true,
          history: true,
          foldGutter: true,
          drawSelection: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          syntaxHighlighting: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: false, // Disable autocomplete - we don't have Python context
          rectangularSelection: true,
          crosshairCursor: false,
          highlightActiveLine: true,
          highlightSelectionMatches: true,
          closeBracketsKeymap: true,
          defaultKeymap: true,
          searchKeymap: true,
          historyKeymap: true,
          foldKeymap: true,
          completionKeymap: false,
          lintKeymap: false,
        }}
        style={{
          fontSize: "13px",
          fontFamily: "monospace",
        }}
      />
      {!readOnly && (
        <div
          style={{
            backgroundColor: "var(--bg-secondary)",
            borderTop: "1px solid var(--border-primary)",
            padding: "4px 8px",
            fontSize: "11px",
            color: "var(--text-tertiary)",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>Ctrl/Cmd+F to search • Ctrl/Cmd+H to replace</span>
          <span>{value.split("\n").length} lines</span>
        </div>
      )}
    </div>
  );
};

export default CodeMirrorEditor;
